import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ConditionalRendering } from '@components/common/conditional-rendering';
import { CurrencyInput, ValidationErrorMessage } from '@components/common/form';
import { INPUT_TYPES } from '@components/common/form/constants/fieldTypes';
import Tooltip from '@components/common/tooltip/Tooltip';
import { PlAN_LEVEL } from '@constants/enrollmentList/enrollmentList.constant';
import { usePermissions } from '@hooks/usePermissions';
import useValidateAPTCAmount from '@hooks/useValidateAPTCAmount';
import { currencyChange } from '../../common/enrollmentUtils';
import { IEnrollmentPremiumDto } from '../../common/types';
import { EnrollmentContext } from '../../context/MemberMyEnrollmentProvider';
import { StyledField } from '../../styles/EnrollmentSharedStyled';

const ColumnMaxAptc = ({ premium }: { premium: IEnrollmentPremiumDto }) => {
    const permissions = useSelector((state: any) => state.memberEnrollment?.permissions);
    const { userPermissions } = permissions || {};
    const { viewEditableFields } = usePermissions();
    const validateAptc = useValidateAPTCAmount();
    const { enrollmentPremiumFormContext, selectedEnrollment } = useContext(EnrollmentContext);
    const {
        watch,
        setValue,
        trigger,
        formState: { errors },
    } = enrollmentPremiumFormContext;

    const values = watch();
    const { isEnrollmentPendingOrPendCancelled } = usePermissions();

    const isDisabled = useMemo(
        () =>
            !premium.isFinancial ||
            selectedEnrollment.planLevel === PlAN_LEVEL.CATASTROPHIC ||
            (!selectedEnrollment.isStateSubsidyEnabled && !premium.isFinancial) ||
            (selectedEnrollment.isStateSubsidyEnabled && !premium.isAptcEligible) ||
            (premium.isFinancial && !premium.isAptcEligible) ||
            !userPermissions?.memberEnrlmntGroupMaxAptcEditable ||
            isEnrollmentPendingOrPendCancelled(selectedEnrollment),

        [premium.isFinancial, premium.isAptcEligible, premium.isAptcEligible],
    );

    const onMaxAptcChange = e => {
        validateAptc({ ...premium, maxAptc: Number(parseFloat(e.target.value).toFixed(2)) });
        currencyChange(setValue, 'maxAptc', premium, e.target.value);
        trigger([`enrollmentPremiumDtoList.${premium.month - 1}`]);
    };

    const convertToDollarValue = e => {
        const rawValue = e.target.value;

        const endsInDotOrZero = rawValue.endsWith('.') || rawValue.endsWith('.0') || rawValue.endsWith('.00');

        if (endsInDotOrZero) {
            const parsed = parseFloat(rawValue);
            if (!isNaN(parsed)) {
                const formatted = parsed.toFixed(2);
                currencyChange(setValue, 'maxAptc', premium, formatted);
            }
        }
    };

    return (
        <ConditionalRendering isTrue={viewEditableFields(userPermissions, values, premium)}>
            <StyledField>
                <CurrencyInput
                    // @ts-ignore
                    label={{
                        wrapperClasses: [''],
                        labelClasses: [''],
                        labelText: '',
                        showRequired: false,
                        isInputRequired: false,
                    }}
                    input={{
                        id: 'maxAptc',
                        isInputRequired: true,
                        type: INPUT_TYPES.TEXT,
                        value: premium.maxAptc,
                        disabled: isDisabled,
                        dataTestId: 'maxAptc',
                        name: 'maxAptc',
                        placeholder: '',
                    }}
                    actions={{
                        handleChange: value => onMaxAptcChange(value),
                        handleBlur: e => convertToDollarValue(e),
                    }}
                    maskOptions={{
                        allowDecimal: true,
                        prefix: '',
                        decimalLimit: '2',
                        integerLimit: 10,
                        includeThousandsSeparator: false,
                        allowLeadingZeroes: true,
                    }}
                    showIcon={true}
                />

                <ConditionalRendering isTrue={!premium.isFinancial}>
                    <Tooltip
                        index={`groupMaxAPTCTooltip-${premium.month}`}
                        id="groupMaxAPTC"
                        elementId="groupMaxAPTCTooltip"
                        content="member.enrollment_list.premiumhistory.tooltip.aptc"
                        place="top"
                        icon={faQuestionCircle}
                        dataAutomationId="groupMaxAPTCTooltip"
                    />
                </ConditionalRendering>
            </StyledField>
            <ConditionalRendering isTrue={premium.isFinancial}>
                <ValidationErrorMessage
                    isVisible={errors?.enrollmentPremiumDtoList?.[premium.month - 1]?.maxAptc}
                    messageText={errors?.enrollmentPremiumDtoList?.[premium.month - 1]?.maxAptc?.message}
                />
            </ConditionalRendering>
        </ConditionalRendering>
    );
};

export default ColumnMaxAptc;
